import { useCallback, useEffect, useState } from 'react'

import type { Impersonation } from '../admin/AccountImpersonation'
import type { Profile } from '~/lib/api/types/users'

interface LocalStorage  {
  [key: `session-replay-withdrawn:${string}`]: string | null
  profile: Profile | null
  accessToken: string | null
  refreshToken: string | null
  referrer: string | null
  impersonation: Impersonation | null
  adminBanner: boolean | null
  preferredEnvironment: 'production' | 'staging' | null
  adminTalkImportData: Record<string, unknown>[] | null
  'discover::organise-promo': boolean | null
  'landing::archivist-promo': boolean | null
  'organiser:has-dismissed-checklist': boolean | null
  'slides::publish-promo': boolean | null
  [tourKey: `tour:${string}`]: number | null
  [alertKey: `__alert:${string}`]: number | null
  // '__alert:{fingerprint}': number | null
}

interface LocalStorageEventDetail<K extends keyof LocalStorage> {
  key: K
  newValue: string | null
}

const localStorageEventName = (key: keyof LocalStorage) => `__localstorage:${key}`

const createLocalStorageEvent = <K extends keyof LocalStorage>(key: K, newValue: string | null) =>
  new CustomEvent<LocalStorageEventDetail<K>>(localStorageEventName(key), { detail: { key, newValue } })

type LocalStorageEvent = ReturnType<typeof createLocalStorageEvent>

export const serializeValue = (value: unknown, parseJson: boolean): string => {
  return parseJson ? JSON.stringify(value) : value as string
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export const deserializeValue = <T = LocalStorage>(rawValue: string | null | undefined, parseJson: boolean): T | null => {
  // if (rawValue === 'undefined') return null
  if (rawValue === null || rawValue === undefined) return null
  //console.debug('Deserializing value from localStorage', { rawValue, parseJson })
  return (parseJson ? JSON.parse(rawValue) : rawValue) as T
}

export const getLocalStorageItem = (key: keyof LocalStorage, defaultSerializedValue?: string): string | null => {
  //console.debug('Getting localStorage item', { key, defaultSerializedValue })
  try {
    const value = window.localStorage.getItem(key)
    if (value === 'undefined') {
      // Failure mode where 'undefined' string was stored
      //console.debug(`Deleting bad 'undefined' value in localStorage for key: ${key}.`)
      clearLocalStorageItem(key)
      return defaultSerializedValue ?? null
    }
    return value
  } catch (err) {
    console.error(err)
    return defaultSerializedValue ?? null
  }
}

export const setLocalStorageItem = (key: keyof LocalStorage, serializedValue: string) => {
  // console.debug('Setting localStorage item', { key, serializedValue })
  try {
    window.localStorage.setItem(key, serializedValue)
    return true
  } catch (err) {
    console.error(err)
    return false
  }
}

export const clearLocalStorageItem = (key: keyof LocalStorage) => {
  // console.log('Clearing localStorage item', { key })
  try {
    window.localStorage.removeItem(key)
  } catch (err) {
    console.log(err)
  }
}

export const useLocalStorage = <K extends keyof LocalStorage>(
  key: K,
  initialValue: LocalStorage[K],
  parseJson: boolean,
): [
  LocalStorage[K] | null,
  (value: LocalStorage[K] | null) => void,
  () => void,
] => {
  const _deserializeValue = useCallback((rawValue: string | null) => {
    try {
      return deserializeValue<LocalStorage[K]>(rawValue, parseJson)
    } catch (err) {
      if (err instanceof SyntaxError) {
        // Looks like the value was not json though we expect json so delete it
        console.debug(`Deleting bad json value in localStorage for key: ${key} = ${rawValue}.`)
        clearLocalStorageItem(key)
      }
      return null
    }
  }, [key, parseJson])

  const rawValue = getLocalStorageItem(key) ?? (initialValue ? serializeValue(initialValue, parseJson) : null)
  const [value, _setValue] = useState<LocalStorage[K]>(() => _deserializeValue(rawValue))

  const setValue = useCallback((value: LocalStorage[K]) => {
    _setValue(value)

    if (value === null) {
      clearLocalStorageItem(key)
      // Notify other hook instances in the same window
      window.dispatchEvent(createLocalStorageEvent(key, null))
      return
    }
    const stringValue = serializeValue(value, parseJson)
    setLocalStorageItem(key, stringValue)
    // Notify other hook instances in the same window
    window.dispatchEvent(createLocalStorageEvent(key, stringValue))
  }, [key, parseJson])

  const clearValue = useCallback(() => {
    _setValue(null)
    clearLocalStorageItem(key)
  }, [key])

  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === key && event.newValue !== rawValue)
        _setValue(_deserializeValue(event.newValue))
    }

    const handleLocalEvent = (event: Event) => {
      const custom = event as LocalStorageEvent
      if (custom.detail.key === key) _setValue(_deserializeValue(custom.detail.newValue))
    }

    window.addEventListener('storage', handleStorageEvent)
    window.addEventListener(localStorageEventName(key), handleLocalEvent)
    return () => {
      window.removeEventListener('storage', handleStorageEvent)
      window.removeEventListener(localStorageEventName(key), handleLocalEvent)
    }
  }, [key, rawValue, _deserializeValue])

  useEffect(() => {
    _setValue(_deserializeValue(rawValue))
  }, [rawValue, _deserializeValue])

  return [value, setValue, clearValue]
}

export const useLocalProfile = () => useLocalStorage('profile', null, true)
export const useLocalAccessToken = () => useLocalStorage('accessToken', null, false)
export const useLocalRefreshToken = () => useLocalStorage('refreshToken', null, false)
export const useLocalReferrer = () => useLocalStorage('referrer', null, false)
export const useLocalImpersonation = () => useLocalStorage('impersonation', null, true)
export const useLocalAdminBanner = () => useLocalStorage('adminBanner', true, true)
export const useLocalPreferredEnvironment = () => useLocalStorage('preferredEnvironment', null, true)
export const useLocalAdminTalkImportData = () => useLocalStorage('adminTalkImportData', null, true)

export default useLocalStorage
